import React, { MutableRefObject, useEffect, useMemo, useRef } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Box, Flex } from "@nubeteck/components";
import { Header, MobileMenu } from "../../layout";
import { MainContainer } from "./main-layout.styled";
import {
  useGetIsTransferActiveMutation,
  useGetUserTeacherInfoMutation,
  useGetPeriodsWithoutRulesMessageQuery,
  useGetUserInfoMutation,
} from "src/services";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/core/store/store";
import { setUserInfo, setIsATeacher, setIsAdmin } from "src/core/store/slices/userSlice";
import {
  decodeJwt,
  getIsUserImpersonated,
  getRolesByToken,
  getRolesByTokenImpersonated,
} from "src/utils";
import { Alert, Button } from "antd";
import { useMediaQuery } from "src/hooks";
import { IMenuItem } from "src/Interfaces/options";
import { studentMenuItems, teacherMenuItems } from "src/constants";

const MainLayout = () => {
  const [isMenuMobileVisible, setMenuMobileVisible] = React.useState(false);
  const [getUserInfo, { data: userInfo }] = useGetUserInfoMutation();
  const { userInfo: userInfoActual } = useSelector((state: RootState) => state.user);
  const [getTeacherUserInfo, { data: teacherUserInfo }] = useGetUserTeacherInfoMutation();
  const periodsWithoutRulesMessageInfo = useGetPeriodsWithoutRulesMessageQuery("", {
    refetchOnMountOrArgChange: true,
  });
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 1180px)");
  const [getIsTransferActive, { data: isTransferActive }] = useGetIsTransferActiveMutation();

  const matriculaAdministrativa = localStorage.getItem("matriculaAdministrativa") === "true";
  const token = localStorage.getItem("token");
  const roles = matriculaAdministrativa
    ? getRolesByTokenImpersonated(token ?? "")
    : getRolesByToken(token ?? "");
  const isAdmin =
    roles.includes("administrador(a) del sistema") ||
    roles.includes("cajero") ||
    roles.includes("vicerrectoria") ||
    roles.includes("retiro administrativo") ||
    roles.includes("perfil financiero") ||
    roles.includes("matricula administrativa") ||
    roles.includes("readonly");
  matriculaAdministrativa;
  const isUserImpersonated = getIsUserImpersonated(localStorage.getItem("token") ?? "");

  const isATeacher = useMemo(() => roles.includes("profesor"), [roles]);

  const userImpersonated: MutableRefObject<any> = useRef(null);

  const adminMenuItems: (IMenuItem | null)[] = [
    {
      name: "Inicio",
      link: "/",
      icon: "",
      roles: [
        "cajero",
        "administrador(a) del sistema",
        "perfil financiero",
        "vicerrectoria",
        "matricula administrativa",
        "retiro administrativo",
        "readonly",
      ],
      tooltip: "Inicio",
    },
    {
      name: "Patrocinios",
      link: "/admin/sponsorships",
      icon: "",
      roles: ["perfil financiero", "readonly"],
      tooltip: "Patrocinios",
    },
    isTransferActive?.data
      ? {
          name: "Revisiones",
          link: "/admin/reviews",
          icon: "",
          roles: ["revisor de retiros", "administrador(a) del sistema"],
          tooltip: "Revisiones",
        }
      : null,
    {
      name: "Importación",
      link: "/admin/data-imports",
      icon: "",
      roles: ["vicerrectoria"],
      tooltip: "Importaciones de datos",
    },
    {
      name: "Caja",
      link: "/admin/charges",
      icon: "",
      roles: ["cajero"],
      tooltip: "Caja administrativa",
    },
    {
      name: "Configuración",
      link: "/admin/settings",
      icon: "",
      roles: ["administrador(a) del sistema"],
      tooltip: "Configuraciones",
    },
    {
      name: "Ingresar como",
      link: "admin/user-impersonation",
      icon: "",
      roles: ["administrador(a) del sistema"],
      tooltip: "Ingresar como usuario estudiante",
    },
    {
      name: "Ingreso prof.",
      link: "admin/teacher-impersonation",
      icon: "",
      roles: ["director de escuela"],
      tooltip: "Ingresar como usuario docente",
    },
    {
      name: "Pagos",
      link: "admin/payments-comparison",
      icon: "",
      roles: ["perfil financiero"],
      tooltip: "Comparaciones de pagos",
    },
    {
      name: "Pre-matrícula",
      link: "admin/user-selection",
      icon: "",
      roles: ["matricula administrativa"],
      tooltip: "Pre-matrícula",
    },
    {
      name: "Retiro adm",
      link: "/admin/subjects-withdraw",
      icon: "",
      roles: ["retiro administrativo"],
      tooltip: "Retiro administrativo",
    },
  ];
  useEffect(() => {
    dispatch(setIsATeacher(isATeacher));
    dispatch(setIsAdmin(isAdmin));
  }, [dispatch, isATeacher, isAdmin]);

  useEffect(() => {
    getTeacherUserInfo("");
    getUserInfo("");
  }, [getUserInfo, getTeacherUserInfo]);

  useEffect(() => {
    if (teacherUserInfo) {
      dispatch(setUserInfo({ ...userInfo?.data, ...teacherUserInfo }));
    } else if (isAdmin && userInfo?.data) {
      dispatch(
        setUserInfo({ ...userInfoActual, hayProcesoAbierto: userInfo?.data?.hayProcesoAbierto }),
      );
    } else if (userInfo?.data) {
      getIsTransferActive();

      dispatch(setUserInfo(userInfo?.data));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getIsTransferActive, isATeacher, isAdmin, userInfo, teacherUserInfo]);

  useEffect(() => {
    const tokenT = localStorage.getItem("token");
    if (!localStorage.getItem("token")) {
      navigate("/login");
    }
    userImpersonated.current = getIsUserImpersonated(tokenT ?? "");
  }, [navigate]);

  const getUserName = () => {
    return matriculaAdministrativa && userImpersonated.current
      ? JSON.parse(decodeJwt(userImpersonated.current)).name
      : userInfo?.data?.fullName;
  };

  const getMenuByAdminRoles = (menuItems: (IMenuItem | null)[]) => {
    const result = menuItems?.filter((menu) => menu?.roles?.find((rol) => roles?.includes(rol)));
    return result;
  };

  const logoutImpersonated = () => {
    localStorage.setItem("token", userImpersonated.current);
    localStorage.setItem("matriculaAdministrativa", "false");
    localStorage.setItem("refreshToken", JSON.stringify(userImpersonated.current.refreshToken));
    getUserInfo("");
    navigate("/");
    window.location.reload();
  };

  const logout = () => {
    localStorage.setItem("matriculaAdministrativa", "false");
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    navigate("/login");
  };

  return (
    <Box>
      <MobileMenu
        isFakeSelecting={matriculaAdministrativa}
        logoutImpersonated={logoutImpersonated}
        menuItems={
          isATeacher
            ? teacherMenuItems
            : isAdmin
            ? getMenuByAdminRoles(adminMenuItems)
            : studentMenuItems
        }
        userName={getUserName()}
        logout={logout}
        isHelpVisible={!isAdmin}
        isVisible={isMenuMobileVisible}
        hide={() => {
          setMenuMobileVisible(false);
        }}
        profiles={
          localStorage.getItem("profiles")
            ? JSON.parse(localStorage.getItem("profiles") ?? "{}")
            : {}
        }
        isATeacher={isATeacher}
        isAdmin={isAdmin}
      />
      <Header
        isFakeSelecting={matriculaAdministrativa}
        logoutImpersonated={logoutImpersonated}
        menuItems={
          isATeacher
            ? teacherMenuItems
            : isAdmin
            ? getMenuByAdminRoles(adminMenuItems)
            : studentMenuItems
        }
        userName={getUserName()}
        isHelpVisible={!isAdmin}
        logout={logout}
        openMenu={() => {
          setMenuMobileVisible(true);
        }}
      />
      {!isUserImpersonated && !!periodsWithoutRulesMessageInfo.data?.data ? (
        <div style={{ marginTop: isMobile ? 80 : 60, marginBottom: -80, height: 50 }}>
          <Alert
            message={periodsWithoutRulesMessageInfo.data?.data}
            type="error"
            style={{ position: "fixed", minWidth: "100%", zIndex: 1000 }}
          />
        </div>
      ) : null}
      <MainContainer
        style={{ paddingTop: isMobile && userImpersonated.current ? "250px" : "auto" }}
      >
        {userImpersonated.current ? (
          <>
            <br />
            <Flex
              style={{
                display: "flex",
                alignItems: isMobile ? "start" : "center",
                gap: isMobile ? "7px" : "15px",
                flexDirection: isMobile ? "column" : "row",
                backgroundColor: "#c9e1ff",
                position: "absolute",
                top: isMobile ? "80px" : "63px",
                left: 0,
                width: "100%",
                justifyContent: "center",
                padding: isMobile ? "10px" : "0px",
              }}
            >
              <Alert
                message={
                  matriculaAdministrativa
                    ? `Matrícula del estudiante ${userInfo?.data.fullName} - ${userInfo?.data.id}`
                    : `El usuario del ${userInfo?.data?.id ? "estudiante" : "docente"} ${userInfo
                        ?.data.fullName} - ${userInfo?.data
                        .id} está siendo utilizado por ${JSON.parse(
                        decodeJwt(userImpersonated.current),
                      )?.name}`
                }
                type="info"
                style={{ backgroundColor: "transparent", border: "none" }}
                showIcon
              />
              <Button onClick={logoutImpersonated}>
                {matriculaAdministrativa ? "Salir" : "Volver a tu usuario"}
              </Button>
            </Flex>
          </>
        ) : // </Tooltip>
        null}
        <Outlet />
      </MainContainer>
    </Box>
  );
};

export default MainLayout;
